import {TextField} from '@hconnect/common/components'
import {ControlledFormField} from '@hconnect/common/types'
import {Stack} from '@mui/material'
import React from 'react'
import {FieldValues, Path, useController, useWatch} from 'react-hook-form'

import {useWidgetTranslation} from '../../hooks'
import {YesNoFormValue} from '../../types'

import {YesNoField} from './YesNoField'

type YesNoItemWithRetakeAndCommentFieldProps<TField extends FieldValues> =
  ControlledFormField<TField> & {
    retakeLabel?: string
    commentLabel?: string
  }

export const YesNoItemWithRetakeAndCommentField = <TField extends FieldValues>({
  label,
  name,
  control,
  disabled,
  readOnly
}: YesNoItemWithRetakeAndCommentFieldProps<TField>) => {
  const {t} = useWidgetTranslation()

  const value = useWatch({control, name: `${name}.value` as Path<TField>})
  const retake = useWatch({control, name: `${name}.valueRetake` as Path<TField>})

  const commentController = useController({
    control,
    name: `${name}.comment` as Path<TField>
  })

  const isNo = (candidate: YesNoFormValue | null | undefined): boolean =>
    candidate === YesNoFormValue.NO

  const showComment = isNo(value) || isNo(retake)

  const handleValueChange = (_e: React.ChangeEvent<HTMLInputElement>, newValue: YesNoFormValue) => {
    const bothWillBeYes = newValue === YesNoFormValue.YES && retake === YesNoFormValue.YES
    if (bothWillBeYes && commentController.field.value) {
      commentController.field.onChange(undefined)
    }
  }

  const handleRetakeChange = (
    _e: React.ChangeEvent<HTMLInputElement>,
    newRetake: YesNoFormValue
  ) => {
    const bothWillBeYes = value === YesNoFormValue.YES && newRetake === YesNoFormValue.YES
    if (bothWillBeYes && commentController.field.value) {
      commentController.field.onChange(undefined)
    }
  }

  return (
    <Stack spacing={1}>
      <YesNoField
        label={label}
        name={`${name}.value` as Path<TField>}
        control={control}
        disabled={disabled}
        readOnly={readOnly}
        onChange={handleValueChange}
      />

      <YesNoField
        label=""
        name={`${name}.valueRetake` as Path<TField>}
        control={control}
        disabled={disabled}
        readOnly={readOnly}
        onChange={handleRetakeChange}
      />

      {showComment && (
        <TextField
          name={`${name}.comment` as Path<TField>}
          label={t('label.comment')}
          control={control}
          disabled={disabled}
          readOnly={readOnly}
        />
      )}
    </Stack>
  )
}
